/* eslint-disable */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Search, ShieldCheck, SlidersHorizontal } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import DiscordServerCard from "../components/DiscordServerCard";
import DiscordMarketView from "../components/DiscordMarketView";
import DiscordTierView from "../components/DiscordTierView";
import { DISCORD_SORT_OPTIONS, fetchDiscordServers, fetchDiscordServersBulk } from "./discordApi";
import "./discord.css";

const PAGE_SIZE = 60;

// Market Mind packs a blob, so a deep tail adds nothing; the Tier Map needs the
// whole set or its smallest buckets look empty.
const MARKET_MAX = 1500;
const TIER_MAX = 8000;

function DiscordCardSkeleton() {
  return (
    <div className="discord-card discord-card-skeleton">
      <div className="discord-hero discord-skel-shimmer" />
      <div className="discord-card-body">
        <div className="discord-skel-line discord-skel-shimmer" style={{ width: "70%", height: 16 }} />
        <div className="discord-skel-line discord-skel-shimmer" style={{ width: "100%", height: 12, marginTop: 10 }} />
        <div className="discord-skel-line discord-skel-shimmer" style={{ width: "85%", height: 12, marginTop: 6 }} />
      </div>
    </div>
  );
}

export default function Discord() {
  const { currentUser } = useAuth();
  const isAdmin = !!currentUser?.is_admin;

  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("members");
  const [order, setOrder] = useState("desc");
  const [verifiedOnly, setVerifiedOnly] = useState(false);
  const [partneredOnly, setPartneredOnly] = useState(false);

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");

  const [viewMode, setViewMode] = useState("cards");
  const [bubbleItems, setBubbleItems] = useState([]);
  const [bubbleLoading, setBubbleLoading] = useState(false);

  const requestIdRef = useRef(0);
  const bubbleRequestIdRef = useRef(0);

  // Debounce the free-text search box.
  useEffect(() => {
    const handle = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(handle);
  }, [searchInput]);

  const queryBase = useMemo(
    () => ({
      search: search || undefined,
      sort,
      order,
      verified: verifiedOnly ? "true" : undefined,
      partnered: partneredOnly ? "true" : undefined,
      limit: PAGE_SIZE,
      // Always request only visible servers here, even if the viewer happens to be an
      // admin — this is the public catalog, not the admin review screen.
      visible: "true",
    }),
    [search, sort, order, verifiedOnly, partneredOnly]
  );

  // Reset + reload whenever filters/sort/search change.
  useEffect(() => {
    const myRequest = ++requestIdRef.current;
    setLoading(true);
    setError("");

    fetchDiscordServers({ ...queryBase, offset: 0 })
      .then((data) => {
        if (requestIdRef.current !== myRequest) return;
        setItems(Array.isArray(data.items) ? data.items : []);
        setTotal(Number(data.total) || 0);
      })
      .catch((err) => {
        if (requestIdRef.current !== myRequest) return;
        setError(String(err.message || err));
        setItems([]);
        setTotal(0);
      })
      .finally(() => {
        if (requestIdRef.current !== myRequest) return;
        setLoading(false);
      });
  }, [queryBase]);

  const handleLoadMore = async () => {
    const myRequest = ++requestIdRef.current;
    setLoadingMore(true);
    try {
      const data = await fetchDiscordServers({ ...queryBase, offset: items.length });
      if (requestIdRef.current !== myRequest) return;
      setItems((prev) => [...prev, ...(Array.isArray(data.items) ? data.items : [])]);
      setTotal(Number(data.total) || 0);
    } catch (err) {
      if (requestIdRef.current !== myRequest) return;
      setError(String(err.message || err));
    } finally {
      if (requestIdRef.current === myRequest) setLoadingMore(false);
    }
  };

  // The bubble layout needs the whole filtered set, so it loads separately.
  useEffect(() => {
    if (viewMode === "cards") return;
    const myRequest = ++bubbleRequestIdRef.current;
    setBubbleLoading(true);

    const { limit, ...rest } = queryBase;
    fetchDiscordServersBulk(rest, viewMode === "tier" ? TIER_MAX : MARKET_MAX)
      .then((data) => {
        if (bubbleRequestIdRef.current !== myRequest) return;
        setBubbleItems(data.items);
      })
      .catch((err) => {
        if (bubbleRequestIdRef.current !== myRequest) return;
        setError(String(err.message || err));
        setBubbleItems([]);
      })
      .finally(() => {
        if (bubbleRequestIdRef.current === myRequest) setBubbleLoading(false);
      });
  }, [viewMode, queryBase]);

  const hasMore = items.length < total;

  return (
    <main className="discord-page">
      <header className="discord-header">
        <div>
          <div className="discord-eyebrow">COMMUNITY SIGNALS</div>
          <h1>Discord Communities</h1>
          <p>Live Discord server metrics tracked across the mindGAME catalog.</p>
        </div>
        <div className="discord-header-right">
          <div className="discord-count">
            <strong>{total.toLocaleString()}</strong>
            <span>Servers</span>
          </div>
          <div className="discord-view-tabs" role="tablist" aria-label="Discord view">
            <button
              type="button"
              role="tab"
              aria-selected={viewMode === "cards"}
              className={`discord-view-tab${viewMode === "cards" ? " is-active" : ""}`}
              onClick={() => setViewMode("cards")}
            >
              Cards
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={viewMode === "market"}
              className={`discord-view-tab${viewMode === "market" ? " is-active" : ""}`}
              onClick={() => setViewMode("market")}
            >
              Market Mind
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={viewMode === "tier"}
              className={`discord-view-tab${viewMode === "tier" ? " is-active" : ""}`}
              onClick={() => setViewMode("tier")}
            >
              Tier Map
            </button>
          </div>
          {isAdmin && (
            <Link to="/admin/discord" className="discord-admin-link">
              <ShieldCheck size={14} /> Review Servers
            </Link>
          )}
        </div>
      </header>

      <div className="discord-controls">
        <div className="discord-search">
          <Search size={15} />
          <input
            type="text"
            placeholder="Search servers..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </div>

        <div className="discord-sort">
          <SlidersHorizontal size={14} />
          <select value={sort} onChange={(e) => setSort(e.target.value)}>
            {DISCORD_SORT_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <select value={order} onChange={(e) => setOrder(e.target.value)}>
            <option value="desc">Descending</option>
            <option value="asc">Ascending</option>
          </select>
        </div>

        <button
          type="button"
          className={`discord-pill${verifiedOnly ? " is-active" : ""}`}
          onClick={() => setVerifiedOnly((v) => !v)}
        >
          Verified
        </button>
        <button
          type="button"
          className={`discord-pill${partneredOnly ? " is-active" : ""}`}
          onClick={() => setPartneredOnly((v) => !v)}
        >
          Partnered
        </button>
      </div>

      {error && <div className="discord-error">Failed to load Discord servers: {error}</div>}

      {viewMode === "market" ? (
        <DiscordMarketView servers={bubbleItems} loading={bubbleLoading} />
      ) : viewMode === "tier" ? (
        <DiscordTierView servers={bubbleItems} loading={bubbleLoading} />
      ) : loading ? (
        <div className="discord-grid">
          {Array.from({ length: 12 }).map((_, i) => <DiscordCardSkeleton key={i} />)}
        </div>
      ) : items.length === 0 ? (
        <div className="discord-empty">No Discord servers match these filters.</div>
      ) : (
        <>
          <div className="discord-grid">
            {items.map((server, index) => (
              <DiscordServerCard key={server.server_id} server={server} rank={index + 1} />
            ))}
          </div>

          {hasMore && (
            <div className="discord-load-more">
              <button type="button" onClick={handleLoadMore} disabled={loadingMore}>
                {loadingMore ? "Loading…" : `Load More (${items.length} of ${total.toLocaleString()})`}
              </button>
            </div>
          )}
        </>
      )}
    </main>
  );
}
